/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  getFees,
  getFeesIdList,
} from '../../interfaces/account/account.graphql';
import {
  FeeIdListItem,
  FeeItem,
} from '../../interfaces/account/account.interface';
import {
  GQLArguments,
  GQLResponse,
  IGQLParams,
  IGQLResponse,
  IGQLSingleResponse,
} from '../../interfaces/graphql.interface';
import {
  getProviderGroupDataForProviderQuery,
  getProviderGroupQuery,
  getProviderGroupQueryByAccounts,
  getProviderGroupQueryByAccountsUser,
  getProviderGroupsQuery,
} from '../../interfaces/provider/provider-group.graphql';
import {
  IProviderGroup,
  IProviderGroupDataForProvider,
  ProviderGroupGridFilters,
  ProviderGroupParams,
} from '../../interfaces/provider/provider-group.interfaces';
import {
  IUserByAccountResponse,
  IUserGroupNested,
} from '../../interfaces/user/user.interface';
import { DEFAULT_PAGE_SIZE } from '../../variables/variables';
import { HelperGQLService } from '../helper.graphql.service';

@Injectable({
  providedIn: 'root',
})
export class ProviderGroupGQLService {
  constructor(
    private apollo: Apollo,
    private helperGQLService: HelperGQLService,
  ) {}

  getProviderGroup(groupId: string): Observable<IProviderGroup> {
    return this.apollo
      .query<IGQLSingleResponse<IProviderGroup>>({
        query: getProviderGroupQuery,
        errorPolicy: 'all',
        fetchPolicy: 'no-cache',
        variables: { groupId },
      })
      .pipe(
        map((response) => {
          return response.data.providerGroup;
        }),
      );
  }

  getProviderGroupDataForProvider(
    groupId: string,
  ): Observable<IProviderGroupDataForProvider> {
    return this.apollo
      .watchQuery<IGQLSingleResponse<IProviderGroupDataForProvider>>({
        query: getProviderGroupDataForProviderQuery,
        errorPolicy: 'all',
        fetchPolicy: 'no-cache',
        variables: { groupId },
      })
      .valueChanges.pipe(
        map((response) => {
          return response.data.providerGroup;
        }),
      );
  }

  getProviderGroups(
    providerGroupsParams?: IGQLParams<ProviderGroupGridFilters>,
    groupIDParams?: any,
  ): Observable<GQLResponse<IProviderGroup>> {
    const { orderby, direction, filter, after } = providerGroupsParams ?? {};
    const sortBy = orderby;
    const sortDirection = direction;

    const gqlArguments: GQLArguments = { first: 50 };

    const groupParams = groupIDParams ? groupIDParams : null;

    if (filter) {
      const groupsFilter: Record<string, unknown> = {
        accountNumber: filter.accountNumber
          ? { eq: filter.accountNumber.toUpperCase() }
          : undefined,
        groupId: filter.groupId
          ? { eq: filter.groupId.toUpperCase() }
          : undefined,
        name: filter.name ? { eq: filter.name.toUpperCase() } : undefined,
        account: filter.accountName
          ? { name: { contains: filter.accountName } }
          : undefined,
        contactName: filter.contactName
          ? { contains: filter.contactName.toUpperCase() }
          : undefined,
        city: filter.addressCity
          ? { eq: filter.addressCity.toUpperCase() }
          : undefined,
        state: filter.addressState
          ? { eq: filter.addressState.toUpperCase() }
          : undefined,
        pms: filter.pMS ? { eq: filter.pMS.toUpperCase() } : undefined,
        ...groupParams,
      };

      gqlArguments.where =
        this.helperGQLService.cleanFilterObject(groupsFilter);
    }

    if (sortBy && sortDirection) {
      gqlArguments.order = [{ [sortBy]: sortDirection }];
    }
    if (after) {
      gqlArguments.after = after;
    }

    return this.apollo
      .watchQuery<IGQLResponse<IProviderGroup>>({
        query: getProviderGroupsQuery,
        fetchPolicy: 'no-cache',
        variables: {
          ...gqlArguments,
          //accountName: filter.accountName
        },
      })
      .valueChanges.pipe(
        map((response) => {
          const a = response.data.providerGroups;
          a.nodes = a.nodes.map((g) => {
            g.isActive =
              g.eCSStatus == 'A' ||
              g.eRAStatus == 'A' ||
              g.claimStatusStatus == 'A' ||
              g.eligibilityStatus == 'A' ||
              g.statementStatus == 'A';
            return g;
          });
          return a;
        }),
      );
  }

  getAllProvidersGroups(
    providerGroupsParams: IGQLParams<ProviderGroupGridFilters>,
    groupIDParams?: IGQLParams<ProviderGroupGridFilters>,
  ): Observable<IProviderGroup[]> {
    return this.helperGQLService.getAllPages<
      IProviderGroup,
      ProviderGroupGridFilters
    >(
      (
        fparams: IGQLParams<ProviderGroupGridFilters>,
        gparams: IGQLParams<ProviderGroupGridFilters>,
      ) => {
        return this.getProviderGroups(fparams, gparams);
      },
      providerGroupsParams,
      groupIDParams,
    );
  }

  getProviderGroupsByAccounts(
    providerGroupParams: ProviderGroupParams,
  ): Observable<GQLResponse<IUserGroupNested>> {
    const gqlArguments: GQLArguments = { first: DEFAULT_PAGE_SIZE };

    const filters = providerGroupParams.accountsCodes.map((code) => {
      return {
        accountNumber: { eq: code },
      };
    });
    const { searchParams } = providerGroupParams;

    let groupsFilters: Record<string, unknown>;

    groupsFilters = { or: filters };

    if (providerGroupParams.allowedGroupsIds) {
      groupsFilters = {
        ...groupsFilters,
        and: {
          or: providerGroupParams.allowedGroupsIds.map((x) => {
            return {
              groupId: { eq: x },
            };
          }),
        },
      };
    }

    if (searchParams) {
      groupsFilters = {
        ...groupsFilters,
        name: { contains: searchParams.name },
        groupId: { contains: searchParams.groupId },
      };
    }

    const gqlFilters = this.helperGQLService.cleanFilterObject(groupsFilters);

    if (gqlFilters) {
      gqlArguments.where = gqlFilters;
    }
    if (providerGroupParams.cursor) {
      gqlArguments.after = providerGroupParams.cursor;
    }

    return this.apollo
      .watchQuery<IGQLResponse<IUserGroupNested>>({
        query: getProviderGroupQueryByAccounts,
        fetchPolicy: 'no-cache',
        variables: {
          ...gqlArguments,
        },
      })
      .valueChanges.pipe(
        map((response) => {
          return response.data.providerGroups;
        }),
      );
  }

  getProviderGroupsByAccountsUser(
    accountIds: string[],
  ): Observable<IUserByAccountResponse> {
    return this.apollo
      .watchQuery<IUserByAccountResponse>({
        query: getProviderGroupQueryByAccountsUser,
        fetchPolicy: 'no-cache',
        variables: {
          accountIds,
        },
      })
      .valueChanges.pipe(
        map((response) => {
          return response.data;
        }),
      );
  }

  getFees(): Observable<FeeItem[]> {
    return this.apollo
      .watchQuery<IGQLResponse<FeeItem>>({
        query: getFees,
        fetchPolicy: 'no-cache',
        errorPolicy: 'ignore',
      })
      .valueChanges.pipe(
        map((response) => {
          return response.data.fees.nodes;
        }),
      );
  }

  getFeesIdList(): Observable<GQLResponse<FeeIdListItem>> {
    const gqlArguments: GQLArguments = {
      first: 100,
      order: { name: 'ASC' },
    };

    return this.apollo
      .query<IGQLResponse<FeeIdListItem>>({
        query: getFeesIdList,
        fetchPolicy: 'no-cache',
        variables: {
          ...gqlArguments,
        },
      })
      .pipe(
        map((response) => {
          return response.data.fees;
        }),
      );
  }
}
